//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { IUrlRouterProvider } from 'angular-ui-router';

import { hasPermissionForTask } from '@services/permissions';

import {
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasAllFeatures,
  lazyLoadModule,
  lazyLoadSubModule,
} from 'app/initialization/routing/stateHelpers';
import { PINTEREST_SCHEDULE_TASK_NAME } from 'app/pinterest/redux/schedule/pinterestScheduleModel';

export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  urlRouterProvider.when(
    '/pinterest/schedule?:targetCuid',
    '/pinterest/schedule/calendar?:targetCuid',
  );

  urlRouterProvider.when('/pinterest/drafts', '/schedule/drafts');

  // Keep this re-direct in case users have bookmarked any v2 routes so that they don't hit our 404 page.
  urlRouterProvider.when(new RegExp(`/pinterest-v2/.*`), '/pinterest');
}

function lazyLoadPinterestBundle(importFn: () => Promise<unknown>) {
  return lazyLoadSubModule('curalate.pinterest', importFn);
}

export const states = {
  pinterest: StateFactory({
    url: '/pinterest',
    template: componentNameToTemplate('pinterestPage'),
    redirectTo: 'pinterest.summary',
    data: {
      title: 'Pinterest',
      navGroup: NavGroup.PUBLISHING,
      hasAccess: () =>
        hasAllFeatures(FeatureNames.Social, FeatureNames.Pinterest),
    },
    resolve: {
      ...lazyLoadModule(
        'curalate.pinterest',
        () => import('@pinterest/PinterestApp'),
      ),
    },
  }),
  'pinterest.summary': StateFactory({
    url: '/summary',
    views: {
      'pinterest-header': {
        template: `<pinterest-summary-header></pinterest-summary-header>`,
      },
      'pinterest-content': {
        template: componentNameToTemplate('pinterestSummary'),
      },
    },
    data: {
      title: 'Pinterest Account',
      navGroup: NavGroup.PUBLISHING,
      hasAccess: () =>
        hasAllFeatures(FeatureNames.Social, FeatureNames.Pinterest),
    },
  }),
  'pinterest.engagement': StateFactory({
    url: '/engagement',
    views: {
      'pinterest-header': {
        template: `<pinterest-header title="Organic Pin Engagement"></pinterest-header>`,
      },
      'pinterest-content': {
        template: componentNameToTemplate('pinterestEngagement'),
      },
    },
    data: {
      title: () => 'Pinterest Account',
      navGroup: NavGroup.PUBLISHING,
      hasAccess: () =>
        hasAllFeatures(FeatureNames.Social, FeatureNames.Pinterest),
    },
  }),
  'pinterest.audience': StateFactory({
    url: '/audience',
    views: {
      'pinterest-header': {
        template: `<pinterest-header title="Audience"></pinterest-header>`,
      },
      'pinterest-content': {
        template: componentNameToTemplate('pinterestAudience'),
      },
    },
    data: {
      title: 'Pinterest Account',
      navGroup: NavGroup.PUBLISHING,
      hasAccess: () =>
        hasAllFeatures(FeatureNames.Social, FeatureNames.Pinterest),
    },
  }),
  'pinterest.schedule': StateFactory({
    url: '/schedule?:targetCuid',
    params: { targetCuid: null },
    views: {
      'pinterest-header': {
        template: `<pinterest-header title="Schedule"></pinterest-header>`,
      },
      'pinterest-content': {
        template:
          '<div ui-view="pinterest-sub" class="flex-tall cu-h-100"></div>',
      },
    },
    data: {
      title: 'Pinterest Account',
      navGroup: NavGroup.PUBLISHING,
      hasAccess: () =>
        hasAllFeatures(FeatureNames.Social, FeatureNames.Pinterest),
    },
  }),
  'pinterest.schedule.calendar': StateFactory({
    url: '/calendar?:targetCuid',
    params: { targetCuid: null },
    views: {
      'pinterest-sub': {
        template: componentNameToTemplate('pinterestSchedulePage'),
      },
    },
    data: {
      title: 'Pinterest Account',
      navGroup: NavGroup.PUBLISHING,
      hasAccess: () =>
        hasAllFeatures(FeatureNames.Social, FeatureNames.Pinterest),
    },
    resolve: {
      ...lazyLoadPinterestBundle(
        () => import('@pinterest/components/schedule/PinterestSchedulePage'),
      ),
    },
  }),
  'pinterest.schedule.select': StateFactory({
    url: '/schedule-type?:postTimeMs',
    data: {
      title: 'Pinterest Account',
      navGroup: NavGroup.PUBLISHING,
      hasAccess: () =>
        hasAllFeatures(FeatureNames.Social, FeatureNames.Pinterest),
    },
    views: {
      'pinterest-sub': {
        template: componentNameToTemplate('pinterestSelectScheduleType'),
      },
    },
    resolve: {
      ...lazyLoadPinterestBundle(
        () =>
          import('@pinterest/components/schedule/PinterestSelectScheduleType'),
      ),
    },
  }),
  'pinterest.schedule.postDetails': StateFactory({
    params: {
      copiedScheduledPostId: null,
      copiedDraftId: null,
      copiedPinId: null,
      scheduleType: null,
      scheduledPostId: null,
      postTimeMs: null,
      targetCuids: null,
    },
    url: '/post-details?:scheduleType&:copiedScheduledPostId&:copiedPinId&:copiedDraftId&:scheduledPostId&:postTimeMs&:targetCuids&:draftPostId&:postType',
    data: {
      title: 'Pinterest Account',
      navGroup: NavGroup.PUBLISHING,
      hasAccess: () =>
        hasAllFeatures(FeatureNames.Social, FeatureNames.Pinterest) &&
        hasPermissionForTask(PINTEREST_SCHEDULE_TASK_NAME),
    },
    views: {
      'pinterest-sub': {
        template: componentNameToTemplate('pinterestPostDetailsPage'),
      },
    },
    resolve: {
      ...lazyLoadPinterestBundle(
        () =>
          import('@pinterest/components/post-details/PinterestPostDetailsPage'),
      ),
    },
  }),
};
